import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { useSocket } from '../../context/SocketContext';
import MessageComposer from './MessageComposer';
import { Bomb, Clock, CornerUpLeft, MessageCircle, Lock, MoreVertical, BellOff, Calendar, Pencil, Trash2, ThumbsUp, ThumbsDown, CheckCircle, Download } from 'lucide-react';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import { Markdown } from '../../utils/markdown';
import { MessageAttachments } from './MessageAttachments';


const SelfDestructTimer = ({ expiresAt, onExpire }: { expiresAt: string, onExpire?: () => void }) => {
    const calculateTimeLeft = () => {
        const difference = +new Date(expiresAt) - +new Date();
        return difference > 0 ? difference : 0;
    };

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
        if (timeLeft <= 0) {
            if (onExpire) onExpire();
            return;
        }

        const timer = setTimeout(() => {
            const nextTime = calculateTimeLeft();
            setTimeLeft(nextTime);
            if (nextTime <= 0 && onExpire) {
                onExpire();
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [timeLeft, onExpire, expiresAt]);

    if (timeLeft <= 0) return null;

    const minutes = Math.floor((timeLeft / 1000 / 60) % 60);
    const seconds = Math.floor((timeLeft / 1000) % 60);

    return (
        <span className="self-destruct-indicator">
            <Clock size={12} />
            {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
        </span>
    );
};

export default function MessageList({ searchQuery }: { searchQuery?: string }) {
    const {
        messages,
        isMessagesLoading,
        openThread,
        activeView,
        markMessageAsRead,
        currentUser,
        setActiveView,
        toggleReaction,
        users,
        markAsUnread,
        snoozeMessage,
        authToken,
        channels,
        directMessages,
        topics
    } = useApp();
    const { typingUsers } = useSocket();

    // Determine if the current view has forced encryption
    const encryptionMode = (() => {
        if (activeView.type === 'channel') {
            return channels.find(c => c.id === activeView.id)?.encryption_mode;
        } else if (activeView.type === 'topic') {
            return topics.find(t => t.id === activeView.id)?.encryption_mode;
        } else if (activeView.type === 'dm') {
            return directMessages.find(d => d.id === activeView.id)?.encryption_mode;
        }
        return null;
    })();

    // isEncryptionForced is computed from settings after settings is declared (see below)

    const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
    const [snoozeMenuOpenId, setSnoozeMenuOpenId] = useState<string | null>(null);


    const { editMessage, deleteMessage, editingMessageId, setEditingMessageId, editContent, setEditContent } = useApp();
    const menuRef = useRef<HTMLDivElement>(null);
    const editingRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const unreadRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const hasInitialScrolled = useRef<string | null>(null);
    const lastTailMessageIdRef = useRef<string | null>(null);
    const [expiredMessageIds, setExpiredMessageIds] = useState<Set<string>>(new Set());
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
    const [isPositioning, setIsPositioning] = useState(true); // start hidden; revealed after scroll is set
    const { settings } = useApp();

    // isEncryptionForced: true if local mode is forced OR admin globally forces E2EE
    const globalChannelForced = activeView.type === 'channel' && settings?.encryption_policy?.channel_mode === 'forced';
    const globalDmForced = activeView.type === 'dm' && settings?.encryption_policy?.dm_mode === 'forced';
    const globalTopicForced = activeView.type === 'topic' && settings?.encryption_policy?.topic_mode === 'forced';
    const isEncryptionForcedGlobal = encryptionMode === 'forced' || globalChannelForced || globalDmForced || globalTopicForced;
    // In the inbox, messages come from many different conversations — we have no local encryption context,
    // so suppress the green tint and lock icon to match how messages look in their native chats.
    const isInbox = activeView.type === 'inbox';

    const firstUnreadIdRef = useRef<string | null>(null);
    const lastViewIdRef = useRef<string | null>(null);

    // Update stable unread marker for the current view
    const firstMsg = messages[0];
    let matchesView = true;
    if (firstMsg) {
        // Exclude inbox snippets (which have sourceType) when we are in a specific chat view
        if (activeView.type === 'channel') matchesView = String(firstMsg.channel_id) === String(activeView.id) && !firstMsg.sourceType;
        else if (activeView.type === 'dm') matchesView = String(firstMsg.dm_id) === String(activeView.id) && !firstMsg.sourceType;
        else if (activeView.type === 'topic') matchesView = String(firstMsg.topic_id) === String(activeView.id) && !firstMsg.sourceType;
        else if (activeView.type === 'ticket') matchesView = String(firstMsg.ticket_id) === String(activeView.id) && !firstMsg.sourceType;
    } else {
        matchesView = false;
    }

    const currentViewKey = `${activeView.type}-${activeView.id}`;

    if (matchesView && lastViewIdRef.current !== currentViewKey) {
        lastViewIdRef.current = currentViewKey;
        const firstUnread = messages.find((m: any) => !(m.read_by || []).some((r: any) => r.user_id === currentUser?.id) && m.author_id !== currentUser?.id);
        firstUnreadIdRef.current = firstUnread ? firstUnread.id : null;
    }
    // Close menu when clicking outside or Escape
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpenId(null);
                setSnoozeMenuOpenId(null);
            }
        };

        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setMenuOpenId(null);
                setSnoozeMenuOpenId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, []);

    useEffect(() => {
        if (editingMessageId && editingRef.current) {
            editingRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [editingMessageId]);

    // Scroll to a specific message (from search result navigation)
    useEffect(() => {
        const targetId = sessionStorage.getItem('scrollToMessageId');
        if (!targetId || messages.length === 0) return;

        const el = document.getElementById(`msg-${targetId}`);
        if (el) {
            // Clear the key so we don't scroll again on next render
            sessionStorage.removeItem('scrollToMessageId');
            setTimeout(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.style.transition = 'background 0.3s';
                el.style.background = 'rgba(99,102,241,0.18)';
                setTimeout(() => { el.style.background = ''; }, 2000);
            }, 150);
        }
    }, [messages, activeView.id, activeView.type]);

    // Tracks which view we need to initial-scroll for.
    const pendingScrollViewId = useRef<string | null>(null);

    // When the active view changes, hide the list and reset scroll intent.
    useEffect(() => {
        pendingScrollViewId.current = `${activeView.type}-${activeView.id}`;
        hasInitialScrolled.current = null;
        lastTailMessageIdRef.current = null;
        setIsPositioning(true);
    }, [activeView.type, activeView.id]);


    // Handle initial scroll (to unread divider or bottom) and auto-scroll for new messages.
    useEffect(() => {
        if (messages.length === 0 || !matchesView) return;
        const currentViewKey = `${activeView.type}-${activeView.id}`;
        const tailMessageId = String(messages[messages.length - 1].id);
        const previousTailMessageId = lastTailMessageIdRef.current;
        const isNewTailAppended = previousTailMessageId !== null
            && previousTailMessageId !== tailMessageId
            && messages.slice(0, -1).some(message => String(message.id) === previousTailMessageId);

        lastTailMessageIdRef.current = tailMessageId;

        // 1. Initial scroll: we have a pending intent AND haven't executed it yet
        if (pendingScrollViewId.current === currentViewKey && hasInitialScrolled.current !== currentViewKey) {
            hasInitialScrolled.current = currentViewKey;

            const targetUnreadId = firstUnreadIdRef.current;

            if (targetUnreadId) {
                // --- CASE A: Unread messages — poll for the divider ---
                let attempts = 0;
                const tryScrollToUnread = () => {
                    if (unreadRef.current) {
                        unreadRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
                        setIsPositioning(false);
                        return;
                    }
                    if (++attempts < 40) {
                        setTimeout(tryScrollToUnread, 50);
                    } else {
                        // Fallback: go to bottom
                        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
                        setIsPositioning(false);
                    }
                };
                requestAnimationFrame(tryScrollToUnread);
            } else {
                // --- CASE B: No unread — anchor to bottom via interval ---
                // Run BEFORE reveal so the first snaps are invisible.

                // Reveal after two frames (scroll is already at bottom, no jump visible)
                requestAnimationFrame(() => requestAnimationFrame(() => {
                    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
                    setIsPositioning(false);
                }));
            }
            return;
        }

        // 2. Only auto-scroll when a new final message was appended. Message object
        // replacements (read receipts, reactions, decryption) retain the same tail ID.
        if (!isNewTailAppended) return;

        const list = listRef.current;
        if (list) {
            const isNearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 150;
            if (isNearBottom && bottomRef.current) {
                bottomRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [messages, activeView.id, activeView.type]);

    const handleSnooze = (messageId: string, durationMinutes: number) => {
        const until = new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();
        snoozeMessage(messageId, until);
        setMenuOpenId(null);
        setSnoozeMenuOpenId(null);
    };

    const formatTime = (dateStr: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
            });
        }
    };

    const renderMessageContent = (message: any) => {
        const isExpiredInState = expiredMessageIds.has(message.id);
        const isExpiredInDate = message.self_destruct && message.expires_at && new Date(message.expires_at).getTime() < Date.now();
        const isExpired = isExpiredInState || isExpiredInDate;

        if (message.self_destruct && isExpired) {
            if (settings?.self_destruct_behavior === 'vanish') return null;
            return (
                <div className="message-text" style={{ fontStyle: 'italic', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, opacity: 0.7 }}>
                    <Bomb size={14} />
                    Message has self-destructed.
                </div>
            );
        }

        if (message.is_deleted) {
            return (
                <div className="message-text" style={{ fontStyle: 'italic', color: 'var(--text-muted)', opacity: 0.7 }}>
                    This message was deleted.
                </div>
            );
        }

        if (editingMessageId === message.id) {
            return (
                <div className="message-edit-mode" style={{ marginTop: 8 }} ref={editingRef}>
                    <MessageComposer
                        initialContent={editContent}
                        initialAttachments={message.attachments || []}
                        initialEncryption={message.is_encrypted}
                        submitLabel="Save"
                        isEditing={true}
                        onCancel={() => {
                            setEditingMessageId(null);
                            setEditContent('');
                        }}
                        onSend={async (content: string, isEncrypted: boolean, attachments: any[], options?: any) => {
                            await editMessage(message.id, content, {
                                is_encrypted: isEncrypted,
                                attachments,
                                self_destruct: options?.selfDestruct
                            });
                            setEditingMessageId(null);
                            setEditContent('');
                        }}
                    />
                </div>
            );
        }

        const isMe = message.author_id === currentUser?.id;
        const isRead = !!message.expires_at; // In our logic, expires_at is set when read

        if (message.self_destruct && !isMe && !isRead) {
            return (
                <div
                    className="self-destruct-trigger"
                    onClick={() => markMessageAsRead(message.id)}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Bomb size={16} color="#e01e5a" />
                        <span style={{ color: '#e01e5a', fontWeight: 600, fontSize: '13px' }}>
                            Confidential: Tap to View
                        </span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: 24 }}>
                        Disappears {message.duration ? message.duration / 60 : 5} mins after viewing
                    </span>
                </div>
            );
        }

        return (
            <div className="message-text" style={message.is_encrypted && !isEncryptionForcedGlobal && !isInbox ? { color: '#2eb67d' } : {}}>
                <Markdown text={message.content || ''} />
                {message.is_edited && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 6 }}>(edited)</span>
                )}
                {message.self_destruct && message.expires_at && !activeView.type.includes('inbox') && (
                    <SelfDestructTimer
                        expiresAt={message.expires_at}
                        onExpire={() => setExpiredMessageIds(prev => {
                            if (prev.has(message.id)) return prev;
                            const next = new Set(prev);
                            next.add(message.id);
                            return next;
                        })}
                    />
                )}
                <MessageAttachments attachments={message.attachments || []} authToken={authToken} />
            </div>
        );
    };

    // Filter messages if search query exists
    const filteredMessages = searchQuery
        ? messages.filter(m => (m.content || '').toLowerCase().includes(searchQuery.toLowerCase()) || (m.author?.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
        : messages;

    // Group messages by date
    const groupedMessages = filteredMessages.reduce((groups: Record<string, any[]>, message) => {
        const date = new Date(message.created_at).toDateString();
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(message);
        return groups;
    }, {});

    if (isMessagesLoading) {
        return (
            <div className="message-list message-list-loading" aria-busy="true" aria-label="Loading messages">
                {[0, 1, 2, 3].map(index => (
                    <div className="message-skeleton" key={index}>
                        <span className="message-skeleton-avatar" />
                        <span className="message-skeleton-lines" />
                    </div>
                ))}
            </div>
        );
    }

    if (messages.length === 0) {
        return (
            <div className="message-list">
                <div className="empty-state">
                    <div className="empty-state-icon"><MessageCircle size={48} strokeWidth={1.5} /></div>
                    <h3 className="empty-state-title">No messages yet</h3>
                    <p className="empty-state-text">
                        Be the first to send a message in this conversation!
                    </p>
                </div>
            </div>
        );
    }

    // For inbox view, show with source info
    if (activeView.type === 'inbox') {
        const handleInboxMessageClick = (message: any) => {
            if (message.channel_id) {
                setActiveView({ type: 'channel', id: message.channel_id });
            } else if (message.topic_id) {
                setActiveView({ type: 'topic', id: message.topic_id });
            } else if (message.dm_id) {
                setActiveView({ type: 'dm', id: message.dm_id });
            } else if (message.ticket_id) {
                setActiveView({ type: 'ticket', id: message.ticket_id });
            }
        };

        return (
            <div className="message-list">
                {messages.map(message => (
                    <div
                        key={message.id}
                        className="message"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleInboxMessageClick(message)}
                    >
                        <div className="message-avatar" style={{ background: getAvatarColor(message.author_id) }}>
                            {message.author?.avatar ? (
                                <img src={message.author.avatar} alt={message.author.name} />
                            ) : (
                                getInitials(message.author?.name)
                            )}
                        </div>
                        <div className="message-content">
                            <div className="message-header">
                                <span className="message-author">
                                    {message.author?.name}
                                    {message.author?.status === 'deleted' && (
                                        <span style={{ fontSize: '0.8em', color: 'var(--text-muted)', marginLeft: 6, fontStyle: 'italic' }}>
                                            (Deleted)
                                        </span>
                                    )}
                                </span>
                                <span className="message-time">{formatTime(message.created_at)}</span>
                                {message.sourceType && (
                                    <span style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--text-muted)',
                                        marginLeft: 'var(--spacing-sm)'
                                    }}>
                                        in {message.sourceType === 'channel' ? `#${message.source?.name}` :
                                            message.sourceType === 'ticket' ? `🎫 ${message.source?.title || 'Unknown Ticket'}` :
                                                'DM'}
                                    </span>
                                )}
                            </div>
                            {renderMessageContent(message)}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <React.Fragment>
            <div
                className="message-list"
                ref={listRef}
                style={{
                    opacity: isPositioning ? 0 : 1,
                    transition: isPositioning ? 'none' : 'opacity 0.15s ease',
                    pointerEvents: isPositioning ? 'none' : undefined,
                }}
            >
                <div ref={contentRef}>
                    {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                        <div key={date} className="message-group">
                        <div className="date-separator">
                            <span>{formatDate(dateMessages[0].created_at)}</span>
                        </div>

                        {dateMessages.map(message => {
                            const isSnoozed = (message.snoozed_by || []).some((s: any) => s.user_id === currentUser?.id);
                            const showUnreadDivider = message.id === firstUnreadIdRef.current;

                            return (
                                <div key={message.id} id={`msg-wrapper-${message.id}`}>
                                    {showUnreadDivider && (
                                        <div ref={unreadRef} className="unread-divider" style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            margin: '16px 0',
                                            color: '#e01e5a',
                                            fontSize: '12px',
                                            fontWeight: 600
                                        }}>
                                            <div style={{ flex: 1, height: 1, background: '#e01e5a', opacity: 0.3 }}></div>
                                            <span style={{ padding: '0 12px' }}>New Messages</span>
                                            <div style={{ flex: 1, height: 1, background: '#e01e5a', opacity: 0.3 }}></div>
                                        </div>
                                    )}
                                    <div id={`msg-${message.id}`} className="message" style={isSnoozed ? { opacity: 0.6 } : {}}>
                                        <div className="message-avatar" style={{ background: getAvatarColor(message.author_id) }}>
                                            {message.author?.avatar ? (
                                                <img src={message.author.avatar} alt={message.author.name} />
                                            ) : (
                                                getInitials(message.author?.name)
                                            )}
                                        </div>
                                        <div className="message-content">
                                            {message.is_broadcast && message.parent_id && (
                                                <div
                                                    style={{
                                                        fontSize: '12px',
                                                        color: '#1d9bd1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        marginBottom: 4,
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => openThread(message.parent_id)}
                                                    className="hover:underline"
                                                >
                                                    <CornerUpLeft size={12} />
                                                    <span>
                                                        <span style={{ fontWeight: 600 }}>Replied to a thread:</span>{" "}
                                                        <span style={{ opacity: 0.9 }}>
                                                            {(() => {
                                                                const parentMsg = messages.find((m: any) => m.id === message.parent_id);
                                                                if (!parentMsg) return "...";
                                                                const text = parentMsg.content || "Attachment";
                                                                return text.length > 35 ? text.substring(0, 35) + "..." : text;
                                                            })()}
                                                        </span>
                                                    </span>
                                                </div>
                                            )}
                                            <div className="message-header">
                                                <span className="message-author">
                                                    {message.author?.name}
                                                    {message.author?.status === 'deleted' && (
                                                        <span style={{ fontSize: '0.8em', color: 'var(--text-muted)', marginLeft: 6, fontStyle: 'italic' }}>
                                                            (Deleted)
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="message-time">{formatTime(message.created_at)}</span>
                                                {message.is_encrypted && !isEncryptionForcedGlobal && !isInbox && (
                                                    <span className="encrypted-indicator" title="End-to-End Encrypted">
                                                        <Lock size={12} />
                                                    </span>
                                                )}
                                                {message.self_destruct && (
                                                    <span className="self-destruct-indicator" title="Self-Destruct Enabled" style={{ marginLeft: 6 }}>
                                                        <Bomb size={12} />
                                                    </span>
                                                )}
                                                {isSnoozed && (
                                                    <span className="snooze-indicator" title="Snoozed" style={{ marginLeft: 6, color: 'var(--text-muted)' }}>
                                                        <Clock size={12} />
                                                    </span>
                                                )}
                                            </div>

                                            {renderMessageContent(message)}

                                            {/* Reactions Display */}
                                            {/* ... (existing reactions code) ... */}
                                            {(message.reactions && (Array.isArray(message.reactions) ? message.reactions.length > 0 : Object.keys(message.reactions).length > 0)) && (
                                                <div className="message-reactions">
                                                    {Object.entries(
                                                        Array.isArray(message.reactions)
                                                            ? message.reactions.reduce((acc: Record<string, string[]>, r: any) => {
                                                                if (!acc[r.emoji]) acc[r.emoji] = [];
                                                                acc[r.emoji].push(r.user_id);
                                                                return acc;
                                                            }, {} as Record<string, string[]>)
                                                            : (message.reactions as Record<string, string[]>)
                                                    ).map(([emoji, userIds]: [string, any]) => {
                                                        const hasReacted = (Array.isArray(userIds) ? userIds : []).includes(currentUser?.id || '');
                                                        return (
                                                            <button
                                                                key={emoji}
                                                                className={`reaction-chip ${hasReacted ? 'active' : ''}`}
                                                                onClick={() => toggleReaction(message.id, emoji)}
                                                            >
                                                                <span>{emoji}</span>
                                                                <span>{userIds.length}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Thread indicator */}
                                            {/* ... (existing thread code) ... */}
                                            {message.reply_count > 0 && (
                                                <div
                                                    className="thread-indicator"
                                                    onClick={() => openThread(message.id)}
                                                >
                                                    <div className="thread-avatars">
                                                        <div className="avatar avatar-sm thread-avatar" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <CornerUpLeft size={10} />
                                                        </div>
                                                    </div>
                                                    <span className="thread-info">
                                                        {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Read Receipts */}
                                            {/* ... (existing read receipt code) ... */}
                                            {(message.read_by && message.read_by.length > 0) && (
                                                <div className="message-read-receipts" style={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    marginTop: 4,
                                                    alignItems: 'center',
                                                    gap: -4,
                                                    paddingRight: 4
                                                }}>
                                                    {(message.read_by || [])
                                                        .filter((r: any) => r.user_id !== currentUser?.id)
                                                        .slice(0, 5)
                                                        .map((r: any) => {
                                                            const user = users.find(u => u.id === r.user_id);
                                                            if (!user) return null;
                                                            return (
                                                                <div
                                                                    key={r.user_id}
                                                                    className="avatar"
                                                                    title={`Seen by ${user.name} at ${formatTime(r.read_at)}`}
                                                                    style={{
                                                                        width: 16,
                                                                        height: 16,
                                                                        fontSize: 8,
                                                                        background: getAvatarColor(user.id),
                                                                        border: '2px solid var(--bg-primary)',
                                                                        marginLeft: -4,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        color: 'white'
                                                                    }}
                                                                >
                                                                    {user.avatar ? <img src={user.avatar} alt={user.name} /> : getInitials(user.name)}
                                                                </div>
                                                            );
                                                        })}
                                                    {message.read_by.filter((r: any) => r.user_id !== currentUser?.id).length > 5 && (
                                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>
                                                            +{message.read_by.filter((r: any) => r.user_id !== currentUser?.id).length - 5}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {activeView.type !== 'inbox' && (
                                            <div className="message-actions">
                                                <button
                                                    className="message-action-btn"
                                                    title="Reply in thread"
                                                    onClick={() => openThread(message.id)}
                                                >
                                                    <CornerUpLeft size={16} strokeWidth={2} />
                                                </button>
                                                <button
                                                    className="message-action-btn"
                                                    title="Thumbs up"
                                                    onClick={() => toggleReaction(message.id, '👍')}
                                                >
                                                    <ThumbsUp size={16} strokeWidth={2} />
                                                </button>
                                                <button
                                                    className="message-action-btn"
                                                    title="Thumbs down"
                                                    onClick={() => toggleReaction(message.id, '👎')}
                                                >
                                                    <ThumbsDown size={16} strokeWidth={2} />
                                                </button>

                                                <button
                                                    className="message-action-btn"
                                                    title="Done"
                                                    onClick={() => toggleReaction(message.id, '✅')}
                                                >
                                                    <CheckCircle size={16} strokeWidth={2} />
                                                </button>

                                                {message.author_id === currentUser?.id && !message.is_deleted && (
                                                    <>
                                                        <button
                                                            className="message-action-btn"
                                                            title="Edit message"
                                                            onClick={() => {
                                                                setEditingMessageId(message.id);
                                                                setEditContent(message.content);
                                                            }}
                                                        >
                                                            <Pencil size={16} strokeWidth={2} />
                                                        </button>
                                                        <button
                                                            className="message-action-btn"
                                                            title="Delete message"
                                                            style={{ color: '#ef4444' }}
                                                            onClick={() => {
                                                                if (window.confirm('Are you sure you want to delete this message?')) {
                                                                    deleteMessage(message.id);
                                                                }
                                                            }}
                                                        >
                                                            <Trash2 size={16} strokeWidth={2} />
                                                        </button>
                                                    </>
                                                )}

                                                <div style={{ position: 'relative' }}>
                                                    <button
                                                        className="message-action-btn"
                                                        title="More options"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMenuOpenId(menuOpenId === message.id ? null : message.id);
                                                            setSnoozeMenuOpenId(null);
                                                        }}
                                                    >
                                                        <MoreVertical size={16} strokeWidth={2} />
                                                    </button>

                                                    {menuOpenId === message.id && (
                                                        <div ref={menuRef} className="dropdown-menu" style={{
                                                            position: 'absolute',
                                                            bottom: '100%',
                                                            right: 0,
                                                            background: 'var(--bg-primary)',
                                                            border: '1px solid var(--border-color)',
                                                            borderRadius: 8,
                                                            boxShadow: 'var(--shadow-lg)',
                                                            zIndex: 100,
                                                            width: 200,
                                                            padding: 4,
                                                            marginBottom: 4
                                                        }}>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    markAsUnread(message.id);
                                                                    setMenuOpenId(null);
                                                                }}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 8,
                                                                    width: '100%',
                                                                    padding: '8px 12px',
                                                                    border: 'none',
                                                                    background: 'none',
                                                                    textAlign: 'left',
                                                                    cursor: 'pointer',
                                                                    color: 'var(--text-primary)',
                                                                    fontSize: 13,
                                                                    borderRadius: 4
                                                                }}
                                                            >
                                                                <BellOff size={14} />
                                                                Mark Unread
                                                            </button>

                                                            <div style={{ position: 'relative' }}>
                                                                <button
                                                                    className="dropdown-item"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSnoozeMenuOpenId(snoozeMenuOpenId === message.id ? null : message.id);
                                                                    }}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 8,
                                                                        width: '100%',
                                                                        padding: '8px 12px',
                                                                        border: 'none',
                                                                        background: 'none',
                                                                        textAlign: 'left',
                                                                        cursor: 'pointer',
                                                                        color: 'var(--text-primary)',
                                                                        fontSize: 13,
                                                                        borderRadius: 4
                                                                    }}
                                                                >
                                                                    <Clock size={14} />
                                                                    Snooze...
                                                                </button>

                                                                {snoozeMenuOpenId === message.id && (
                                                                    <div className="snooze-submenu" style={{
                                                                        position: 'absolute',
                                                                        right: '100%',
                                                                        bottom: 0,
                                                                        background: 'var(--bg-primary)',
                                                                        border: '1px solid var(--border-color)',
                                                                        borderRadius: 8,
                                                                        boxShadow: 'var(--shadow-lg)',
                                                                        zIndex: 101,
                                                                        width: 200,
                                                                        padding: 4,
                                                                        marginRight: 4
                                                                    }}>
                                                                        <div className="submenu-header" style={{ padding: '4px 12px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Snooze until...</div>
                                                                        {[
                                                                            { label: 'Later today (1h)', mins: 60 },
                                                                            { label: 'Tomorrow morning', mins: 24 * 60 - (new Date().getHours() * 60 + new Date().getMinutes()) + 9 * 60 }, // Valid approx
                                                                            { label: 'Next Week', mins: 7 * 24 * 60 }
                                                                        ].map(opt => (
                                                                            <button
                                                                                key={opt.label}
                                                                                className="dropdown-item"
                                                                                onClick={() => handleSnooze(message.id, opt.mins)}
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 8,
                                                                                    width: '100%',
                                                                                    padding: '8px 12px',
                                                                                    border: 'none',
                                                                                    background: 'none',
                                                                                    textAlign: 'left',
                                                                                    cursor: 'pointer',
                                                                                    color: 'var(--text-primary)',
                                                                                    fontSize: 13,
                                                                                    borderRadius: 4
                                                                                }}
                                                                            >
                                                                                <Calendar size={14} />
                                                                                {opt.label}
                                                                            </button>
                                                                        ))}
                                                                        <div className="dropdown-divider" style={{ height: 1, background: 'var(--border-color)', margin: '4px 0' }} />
                                                                        <div style={{ padding: '8px 12px' }}>
                                                                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Custom Date/Time:</div>
                                                                            <input
                                                                                type="datetime-local"
                                                                                className="custom-snooze-input"
                                                                                onChange={(e) => {
                                                                                    if (e.target.value) {
                                                                                        snoozeMessage(message.id, new Date(e.target.value).toISOString());
                                                                                        setMenuOpenId(null);
                                                                                        setSnoozeMenuOpenId(null);
                                                                                    }
                                                                                }}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    padding: '4px 8px',
                                                                                    fontSize: 12,
                                                                                    borderRadius: 4,
                                                                                    border: '1px solid var(--border-color)',
                                                                                    background: 'var(--bg-secondary)',
                                                                                    color: 'var(--text-primary)'
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}


                {/* Typing Indicator */}
                {activeView.id && typingUsers[activeView.id] && typingUsers[activeView.id].length > 0 && (
                    <div className="typing-indicator">
                        {/* ... (existing typing indicator) ... */}
                        <div className="typing-dots">
                            <span className="typing-dot" />
                            <span className="typing-dot" />
                            <span className="typing-dot" />
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            {typingUsers[activeView.id].map((t: any) => t.userName).join(', ')}
                            {typingUsers[activeView.id].length === 1 ? ' is' : ' are'} typing...
                        </span>
                    </div>
                )}
                <div ref={bottomRef} />
                </div>
            </div>

            {/* Image Lightbox Overlay */}
            {
                lightboxUrl && (
                    <div
                        onClick={() => setLightboxUrl(null)}
                        style={{
                            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 9999, cursor: 'zoom-out'
                        }}
                        onKeyDown={(e) => e.key === 'Escape' && setLightboxUrl(null)}
                        tabIndex={0}
                    >
                        <img
                            src={lightboxUrl}
                            alt="Enlarged"
                            onClick={e => e.stopPropagation()}
                            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.7)', cursor: 'default' }}
                        />
                        <a
                            href={lightboxUrl}
                            download
                            onClick={e => e.stopPropagation()}
                            style={{
                                position: 'fixed', top: 20, right: 64,
                                background: 'rgba(255,255,255,0.15)', borderRadius: 8,
                                padding: '8px 16px', color: 'white', textDecoration: 'none',
                                display: 'flex', alignItems: 'center', gap: 6, fontSize: 14
                            }}
                            title="Download image"
                        >
                            <Download size={16} /> Download
                        </a>
                        <button
                            onClick={() => setLightboxUrl(null)}
                            style={{
                                position: 'fixed', top: 20, right: 20,
                                background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
                                padding: '8px 12px', color: 'white', cursor: 'pointer', fontSize: 18
                            }}
                            title="Close"
                        >
                            ✕
                        </button>
                    </div>
                )
            }
        </React.Fragment>
    );
}
